import { useEffect, useState } from "react";
import { Button, Form, Input, Select, Row, Col } from "antd";
import {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../components/form/form";
import { TDropdownOptions, TResponseModel } from "../../../types/config";
import { eResultCode } from "../../../utils/enum";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import {
  GetCountryList,
  GetStateList,
  AddEditState,
  AddDistrict,
  UpdateDistrict,
} from "../../../utils/api.constant";
import Utils from "../../../utils";
import { FormRow } from "../../../components/form/form-row";
import { requiredFieldValidator } from "../../../utils/input.validator";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";

type DrawerProps = {
  id: number;
  country: string;
  state?: string;
  district?: string;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
  formType: "state" | "district";
  stateCode: string;
  aliases: string;
};

interface Location {
  id?: number;
  countryName?: string;
  name?: string;
  stateCode?: string;
}

export default function CustomerForm(props: DrawerProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [countryOptions, setCountryOptions] = useState<TDropdownOptions[]>([]);
  const [stateOptions, setStateOptions] = useState<TDropdownOptions[]>([]);
  const { post } = useFetch();
  const { onShowToast } = useToast();

  useEffect(() => {
    const fetchData = async () => {
      await fetchCountry();
      await fetchStates();
    };

    fetchData(); // Call the fetch function
  }, []);

  useEffect(() => {
    if (stateOptions.length > 0) {
      form.setFieldsValue({
        country: props.country,
        stateId:
          stateOptions.find((option) => option.label === props.state)?.value ||
          "",
        district: props.district || "",
        stateCode: props.stateCode,
        aliases: props.aliases,
      });
    }
  }, [props, stateOptions]);

  // Handle on submit event
  const onFinish = async (values: Location) => {
    try {
      console.log("Form Values:", values);
      // Determine the endpoint based on formType and presence of id
      let endpoint = "";
      if (props.formType === "district") {
        endpoint = props.id ? UpdateDistrict : AddDistrict;
      } else if (props.formType === "state") {
        endpoint = props.id ? AddEditState : AddEditState;
      }

      const payload = {
        data: {
          id: props.id ? props.id : 0,
          ...values,
        },
      };

      setIsLoading(true);

      const response = await post(endpoint, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        form.resetFields();
        props.onRefreshList();
        props.onCloseDrawer();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error during API call:", error);
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.centerTop,
        content: "An error occurred while saving data.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // To get the list of countries
  const fetchCountry = async () => {
    try {
      const payload = {
        data: {},
      };
      setIsLoading(true);
      const response: TResponseModel = await post(GetCountryList, payload);
      if (response) {
        const { dataResponse, data } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          const mappedData = Utils.mapDropdownOptions(data);
          form.setFieldValue("countryId", mappedData[0].value);
          setCountryOptions(mappedData);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  // To get the list of states
  const fetchStates = async () => {
    try {
      const payload = {
        data: {
          countryId: form.getFieldValue("countryId"),
        },
      };
      setIsLoading(true);
      const response: TResponseModel = await post(GetStateList, payload);
      if (response) {
        const { dataResponse, data } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          setStateOptions(Utils.mapDropdownOptions(data));
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form
      form={form}
      name="register"
      onFinish={onFinish}
      autoComplete="off"
      initialValues={{
        country: props.country,
        state: props.state || "",
        district: props.district || "",
        stateCode: props.stateCode,
      }}
      layout="vertical"
    >
      <FormHeader>
        <CloseOutlined
          style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
          onClick={() => {
            props.onCloseDrawer();
          }}
        />
      </FormHeader>
      <FormContent>
        <Row gutter={16} className="formRowHeight">
          <Col span={24}>
            <Form.Item
              name="countryId"
              label="Country"
              rules={[{ required: true, message: "Please choose country" }]}
            >
              <Select
                placeholder="Select country"
                options={countryOptions}
                disabled={props.id > 0}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* State Selection (Visible only for "state" formType) */}
        {props.formType === "state" && (
          <>
            <Row gutter={16} className="formRowHeight">
              <Col span={24}>
                <Form.Item
                  name="state"
                  label="State"
                  rules={[
                    {
                      required: true,
                      validator: requiredFieldValidator("enter state name"),
                    },
                  ]}
                >
                  <Input autoComplete="off" />
                </Form.Item>
              </Col>
            </Row>
            {/* Aliases Field (Visible only in edit mode) */}
            {props.id > 0 && (
              <Row gutter={16} className="formRowHeight">
                <Col span={24}>
                  <Form.Item
                    name="aliases"
                    label="Aliases"
                    rules={[
                      {
                        required: true,
                        validator: requiredFieldValidator("enter aliases"),
                      },
                    ]}
                  >
                    <Input autoComplete="off" />
                  </Form.Item>
                </Col>
              </Row>
            )}
          </>
        )}

        {/* District Selection (Visible only for "district" formType) */}
        {props.formType === "district" && (
          <>
            <Row gutter={16} className="formRowHeight">
              <Col span={24}>
                <Form.Item
                  name="state"
                  label="State"
                  rules={[{ required: true, message: "Please choose state" }]}
                >
                  <Select
                    placeholder="Select state"
                    options={stateOptions}
                    disabled={props.formType === "district" && props.id > 0}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16} className="formRowHeight">
              <Col span={24}>
                <Form.Item
                  name="district"
                  label="District"
                  rules={[
                    {
                      required: true,
                      validator: requiredFieldValidator("choose district"),
                    },
                  ]}
                >
                  <Input autoComplete="off" />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}
      </FormContent>

      <FormFooter className="formFooter">
        <FormRow className="formRow">
          <Button
            type="primary"
            htmlType="submit"
            className="btnDimensions"
            disabled={isLoading}
          >
            {props.id ? "Update" : "Save"}
          </Button>
          <Button
            type="primary"
            className="btnDimensions"
            onClick={props.onCloseDrawer}
          >
            Cancel
          </Button>
        </FormRow>
      </FormFooter>
    </Form>
  );
}
